import React, { useContext } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import FormControls from './FormControls';
import { FormContext } from './FormContext';

const FormControlBar = props => {
  const {
    cancelLabel,
    customActions = [],
    firstStep,
    hasSteps,
    lastStep,
    onCancel,
    onResetForm,
    onSubmit,
    onValidationReport,
    previousStep,
    readOnly,
    saving,
    submitLabel,
    validationIssues,
  } = props;

  const { manager, enabledControls, hideSubmit, hideCancel, hideReset } = useContext(FormContext);

  const cancelLabelText = cancelLabel || 'Cancel';

  const renderButtons = () => {
    const buttons = [];

    // Submit Button
    if (!hideSubmit) {
      let idleText = submitLabel;
      const workingText = 'Please Wait';
      const handler = saving ? () => {} : onSubmit;

      if (hasSteps) {
        idleText = lastStep ? 'Finish / Submit' : 'Continue';
      }

      if (readOnly) {
        idleText = (
          <>
            <i className="zmdi zmdi-lock" /> Read Only
          </>
        );
      }

      buttons.push(
        <Button
          className="form-edit-submit"
          key="submit"
          type="submit"
          variant="primary"
          disabled={saving || !enabledControls || readOnly}
          onClick={handler}
        >
          {saving ? (
            <div className="button-loading">
              <i className="zmdi zmdi-settings zmdi-hc-spin" style={{ color: '#FFF', fontSize: '15px' }} />{' '}
              {workingText}
            </div>
          ) : (
            idleText
          )}
        </Button>
      );

      if (hasSteps && !firstStep) {
        buttons.push(
          <Button key="back" className="form-edit-back" variant="info" onClick={() => previousStep()}>
            <i className="zmdi zmdi-long-arrow-left zmdi-hc-lg" /> Back
          </Button>
        );
      }
    }

    // Issues Button
    if (validationIssues && onValidationReport) {
      buttons.push(
        <Button
          className="form-edit-errors"
          key="issues"
          variant="danger"
          title="Display Issues"
          onMouseDown={onValidationReport}
        >
          <i className="zmdi zmdi-alert-polygon" /> {validationIssues}
        </Button>
      );
    }

    if (!hideReset) {
      buttons.push(
        <Button
          key="reset"
          className="form-edit-reset"
          variant="secondary"
          onClick={onResetForm}
          disabled={!enabledControls}
        >
          Reset
        </Button>
      );
    }

    if (!hideCancel) {
      buttons.push(
        <Button
          key="cancel"
          className="form-edit-cancel"
          variant="secondary"
          onMouseDown={onCancel}
          disabled={!enabledControls}
        >
          {cancelLabelText}
        </Button>
      );
    }

    // Custom Actions
    customActions.forEach((action, key) => {
      const style = {};

      // Render which side the button will be on
      // TODO Improve
      if (action.side) {
        style.float = action.side;
      }

      let actionLabel = action.label;

      if (action.type === 'submit' && saving) {
        actionLabel = (
          <div className="button-loading">
            <i className="zmdi zmdi-settings zmdi-hc-spin" style={{ color: '#FFF', fontSize: '15px' }} /> Please Wait
          </div>
        );
      }

      if (action.type === 'submit' && readOnly) {
        actionLabel = (
          <>
            <i className="zmdi zmdi-lock" /> Read Only
          </>
        );
      }

      const id = action.id || (typeof action.label === 'string' ? action.label : '');

      const handleAction = event => {
        event.preventDefault();
        event.stopPropagation();
        if (action.action) {
          action.action({ onSubmit, manager });
        }
      };

      // Allow custom action submits to capture form's default submit
      const method = action.type === 'submit' ? 'unshift' : 'push';
      const className = classNames(
        'form-cust-btn',
        `form-cust-${id.replace(/\s+/g, '-').toLowerCase()}`,
        action.className
      );

      buttons[method](
        <Button
          key={key}
          className={className}
          variant={action.style || 'secondary'}
          disabled={!enabledControls || (action.type === 'submit' && saving) || (action.type === 'submit' && readOnly)}
          onClick={handleAction}
          style={style}
          title={action.title}
          type={action.type || 'button'}
        >
          {actionLabel}
        </Button>
      );
    });

    return buttons;
  };

  return <FormControls>{renderButtons()}</FormControls>;
};

FormControlBar.propTypes = {
  cancelLabel: PropTypes.string,
  customActions: PropTypes.array,
  firstStep: PropTypes.bool,
  hasSteps: PropTypes.bool,
  lastStep: PropTypes.bool,
  onCancel: PropTypes.func,
  onResetForm: PropTypes.func,
  onSubmit: PropTypes.func,
  onValidationReport: PropTypes.func,
  previousStep: PropTypes.func,
  readOnly: PropTypes.bool,
  saving: PropTypes.bool,
  submitLabel: PropTypes.string,
  validationIssues: PropTypes.number,
};

export default FormControlBar;
